import React from 'react';
import ButtonInc from '../../../../../../shared-ui/ButtonInc';
import { getPartner } from '../../../../../../lib/access.es6';
import { groupMembers } from '../utils/memberHelpers';
import { calculateGroupTotal, formatPremium } from '../utils/calculations';

const SummaryScreen = ({
  policy,
  allMembers,
  membersToRemove,
  membersToAdd,
  remainingMembers,
  finalMembers,
  onBack,
  onConfirm
}) => {
  const currencyCode = getPartner().country.currency_code;

  // Calculate original premium
  const allMembersGrouped = groupMembers(allMembers);
  const oldGrandTotal = calculateGroupTotal(allMembersGrouped.nuclear) +
                        calculateGroupTotal(allMembersGrouped.parents) +
                        calculateGroupTotal(allMembersGrouped.extended);

  // Calculate premium after removals
  const displayGrouped = groupMembers(remainingMembers);
  const afterRemovalTotal = calculateGroupTotal(displayGrouped.nuclear) +
                            calculateGroupTotal(displayGrouped.parents) +
                            calculateGroupTotal(displayGrouped.extended);

  // Calculate added members premium
  const addedMembersTotal = calculateGroupTotal(membersToAdd || []);

  // Calculate final premium (remaining + added)
  const finalGrouped = groupMembers(finalMembers);
  const finalGrandTotal = calculateGroupTotal(finalGrouped.nuclear) +
                          calculateGroupTotal(finalGrouped.parents) +
                          calculateGroupTotal(finalGrouped.extended);

  const hasRemovals = membersToRemove && membersToRemove.length > 0;
  const hasAdditions = membersToAdd && membersToAdd.length > 0;
  const netChange = finalGrandTotal - oldGrandTotal;
  const isIncrease = netChange > 0;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: 'bold', marginBottom: '8px', color: '#032A37' }}>
              Confirm Member Changes
            </h1>
            <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
              {hasRemovals && hasAdditions && 'Removing and adding members'}
              {hasRemovals && !hasAdditions && 'Removing members'}
              {!hasRemovals && hasAdditions && 'Adding members'}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 4px 0' }}>
              <strong>Policy:</strong> {policy.policy_number}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Box */}
      <div style={{
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '25px',
        marginBottom: '30px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', color: '#032A37' }}>
          Amendment Summary
        </h3>

        {/* Changes Summary */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          padding: '15px',
          marginBottom: '15px'
        }}>
          {hasRemovals && (
            <div style={{ marginBottom: '10px' }}>
              <strong>Removed:</strong> {membersToRemove.length} member{membersToRemove.length !== 1 ? 's' : ''}
            </div>
          )}
          {hasAdditions && (
            <div style={{ marginBottom: '10px' }}>
              <strong>Added:</strong> {membersToAdd.length} member{membersToAdd.length !== 1 ? 's' : ''}
            </div>
          )}
          <div>
            <strong>Final Total:</strong> {finalMembers.length} member{finalMembers.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Premium Impact */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '6px',
          padding: '15px',
          marginBottom: '15px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '15px' }}>Original Premium:</span>
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {currencyCode} {formatPremium(oldGrandTotal)}
            </span>
          </div>

          {hasRemovals && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', paddingLeft: '15px', color: '#666' }}>
              <span style={{ fontSize: '14px' }}>After Removals:</span>
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#d32f2f' }}>
                {currencyCode} {formatPremium(afterRemovalTotal)} ({formatPremium(afterRemovalTotal - oldGrandTotal)})
              </span>
            </div>
          )}

          {hasAdditions && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', paddingLeft: '15px', color: '#666' }}>
              <span style={{ fontSize: '14px' }}>Added Members Premium:</span>
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#388e3c' }}>
                +{currencyCode} {formatPremium(addedMembersTotal)}
              </span>
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '10px',
            borderTop: '2px solid #032A37'
          }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#032A37' }}>New Total Premium:</span>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#032A37' }}>
              {currencyCode} {formatPremium(finalGrandTotal)}
            </span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '10px',
            marginTop: '10px',
            borderTop: '1px solid #ddd'
          }}>
            <span style={{ fontSize: '15px', fontWeight: 'bold' }}>Net Change:</span>
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: isIncrease ? '#388e3c' : (netChange < 0 ? '#d32f2f' : '#666')
            }}>
              {isIncrease ? '+' : ''}{currencyCode} {formatPremium(Math.abs(netChange))}
            </span>
          </div>
        </div>

        {/* Removed Members */}
        {hasRemovals && (
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
            <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px', color: '#d32f2f' }}>
              Removed Members ({membersToRemove.length}):
            </p>
            <ul style={{ marginLeft: '20px' }}>
              {membersToRemove.map((member, i) => (
                <li key={i} style={{ marginBottom: '6px', fontSize: '14px' }}>
                  {member.first_name} {member.last_name} ({member.relationship}) - {currencyCode} {formatPremium(member.premium_amount_in_cents)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Added Members */}
        {hasAdditions && (
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
            <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px', color: '#388e3c' }}>
              Added Members ({membersToAdd.length}):
            </p>
            <ul style={{ marginLeft: '20px' }}>
              {membersToAdd.map((member, i) => (
                <li key={i} style={{ marginBottom: '6px', fontSize: '14px' }}>
                  {member.first_name} {member.last_name} ({member.relationship}) - {currencyCode} {formatPremium(member.premium_amount_in_cents)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Final Members List */}
        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
          <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px' }}>
            Final Members List ({finalMembers.length}):
          </p>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <table style={{
              width: '100%',
              fontSize: '13px',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#e0e0e0' }}>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Name</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Relationship</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ccc' }}>Premium</th>
                </tr>
              </thead>
              <tbody>
                {finalMembers.map((member, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{member.first_name} {member.last_name}</td>
                    <td style={{ padding: '8px' }}>{member.relationship}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {currencyCode} {formatPremium(member.premium_amount_in_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
        <ButtonInc
          btnType="secondary"
          onClick={onBack}
          style={{
            border: '1px solid #032A37'
          }}
        >
          Back
        </ButtonInc>
        <ButtonInc
          onClick={onConfirm}
          style={{
            background: '#032A37'
          }}
        >
          Confirm Amendment
        </ButtonInc>
      </div>
    </div>
  );
};

export default SummaryScreen;
